import React, { useEffect, useState } from "react";
import { Col, Container, Modal, Row, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import CommonTitleBar from "components/common/CommonTitleBar";
import { useDispatch, useSelector } from "react-redux";
import { getAllOffers, updateOfferStatus } from "store/actions/offerSlice";
import NoDataFound from "components/common/NoDataFound";
import { Dropdown } from "react-bootstrap";
import { Ellipsis, X } from "lucide-react";
import {
  formatDate,
  getMinMaxDate,
  handleStopPropagation,
} from "components/common/Utils";
import {
  getBranchList,
  getBranchListWithCity,
} from "store/actions/branchSlice";
import Paginations from "components/common/Paginations";
import { showAlert } from "store/reducers/alertReducer";
import Filters from "components/common/Filters";
import TableShimmer from "components/ShimmerEffects/CommonShimmer/TableShimmer";
import Riyal from "assets/images/icons/riyal.svg";
import { getOffersList } from "store/actions/offerSlice";

const Offers = () => {
  const { t } = useTranslation();
  document.title = t("Common | Ashjar Space");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { search } = useLocation();
  const pageNumber = Number(new URLSearchParams(search).get("page")) || 1;

  const {
    isLoading,
    loading,
    perPage,
    totalCount,
    totalPages,
    currentPage,
    offers,
  } = useSelector((state) => state.Offers);
  const { branchesList } = useSelector((state) => state.Branches);
  const [dates, setDates] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    status: -1,
    orderBy: -1,
  });
  const [selectedId, setSelectedId] = useState(null);
  const [showModal, setShowModal] = useState(false); // DELETE MODAL
  const [show, setShow] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [selectedTab, setSelectedTab] = useState(1);
  useEffect(() => {
    const fetchBranchesList = () => {
      dispatch(getBranchList());
    };

    fetchBranchesList();
    dispatch(getBranchListWithCity());
    getAllData();
  }, []);

  const getAllData = (page, action) => {
    const { actionType } = action || {}; // SEARCH, RESET, RESET_KEYWORD
    const { orderBy, status } = selectedFilters;

    const isReset = actionType === "RESET";

    const payload = {
      perPage,
      pageNo: page || pageNumber,
      status: isReset ? -1 : status,
      orderBy: isReset ? -1 : orderBy,
      dateRange: isReset ? {} : getMinMaxDate(dates),
      keyword: ["RESET", "RESET_KEYWORD"].includes(actionType)
        ? ""
        : keyword || "",
    };

    dispatch(getAllOffers(payload));

    // navigate(`/plan/offers?page=${page || pageNumber}`);
  };

  useEffect(() => {
    getAllData();
  }, [selectedTab]);

  const handleStatusChange = (id, status) => {
    setSelectedId(id);
    const payload = {
      id,
      status: status === 1 ? 0 : 1,
    };

    dispatch(updateOfferStatus(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
          setSelectedId(null);
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
          setSelectedId(null);
        }
      })
      .catch((err) => {});
  };

  const getStatusDetails = (status, expiresAt, isNeverEnd) => {
    // const isExpired = isNeverEnd
    //   ? false
    //   : expiresAt && isAfter(new Date(), parseISO(expiresAt));

    // if (isExpired) {
    //   return { label: "Expired", className: "cancelled_status" }; // Example expired style
    // }

    const statusMap = {
      1: { label: "Active", className: "completed_status" },
      0: { label: "Deactive", className: "cancelled_status" },
    };

    return statusMap[status] ?? {};
  };

  const getBranchNames = (applicableBranches) => {
    return (
      branchesList
        ?.filter((branch) => applicableBranches.includes(branch._id))
        ?.map((branch) => branch.name)
        .join(", ") || t("No applicable branches")
    );
  };

  const getSpaceTypeLabel = (spaceType) => {
    const spaceTypeMap = {
      workSpace: t("Work Space"),
      meetingRoom: t("Meeting Room"),
      workShop: t("Workshop"),
    };

    return spaceTypeMap[spaceType] ?? "-";
  };

  const handleReset = () => {
    setKeyword("");
    setDates([]);
    setSelectedFilters({
      status: -1,
      orderBy: -1,
    });
    getAllData(1, { actionType: "RESET" });
  };

  const filters = [{}];

  return (
    <React.Fragment>
      <div className="page-content common_page">
        <Container fluid>
          <CommonTitleBar
            title={t("Plan Offers")}
            isCreateBtn={true}
            createBtnText={t("Add New")}
            onCreateBtnClick={() => navigate("/plan/offers/add-new-offer")}
          />
          <div className="common_wrapper">
            <Filters
              keyword={keyword}
              setKeyword={setKeyword}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              dates={dates}
              setDates={setDates}
              filters={filters}
              onSearch={getAllData}
              onReset={handleReset}
              className="filter_2"
            />
            <Row className="mt-4">
              <Col lg={12}>
                {loading ? (
                  <TableShimmer col={10} />
                ) : offers.length === 0 ? (
                  <NoDataFound />
                ) : (
                  <div
                    className="common_table_style table-responsive"
                    style={{ minHeight: showDropdown ? 300 : "unset" }}
                  >
                    <Table>
                      <thead className="t_head full_th">
                        <tr>
                          <th>#</th>
                          <th>
                            <div className="d-flex align-items-center gap-1">
                              {t("Offer Name")}
                            </div>
                          </th>
                          <th>{t("Discount Type")}</th>
                          <th>{t("Discount Value")}</th>
                          <th>{t("Space Type")}</th>
                          <th>{t("Branches")}</th>
                          <th>{t("Status")}</th>
                          <th>{t("Actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {offers?.map((item, index) => (
                          <tr key={item._id}>
                            <td>{(currentPage - 1) * perPage + index + 1}</td>
                            <td>{item?.name}</td>
                            <td>
                              <p className="text-capitalize">
                                {item?.discountType || "-"}
                              </p>
                            </td>
                            <td>
                              {item?.discountType !== "percentage" && (
                                <img
                                  src={Riyal}
                                  width={15}
                                  height={15}
                                  alt="riyal"
                                  className="me-1"
                                />
                              )}
                              {item?.amount || "-"}
                              {item?.discountType === "percentage" && "%"}
                            </td>

                            <td>{getSpaceTypeLabel(item?.spaceType)}</td>
                            <td>
                              <p style={{ minWidth: 200 }}>
                                {getBranchNames(item?.applicableBranches)}
                              </p>
                            </td>
                            <td>
                              <div>
                                <p
                                  className={
                                    getStatusDetails(
                                      item?.status,
                                      item?.expiresAt,
                                      item?.isNeverEnd
                                    ).className
                                  }
                                >
                                  {
                                    getStatusDetails(
                                      item?.status,
                                      item?.expiresAt,
                                      item?.isNeverEnd
                                    ).label
                                  }
                                </p>
                                {item?.isActive === 0 && (
                                  <p className="cancelled_status mt-1">
                                    {t("Expired")}
                                  </p>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="d-flex align-items-center gap-3">
                                <Dropdown
                                  className="custom_dropdown list_action_dropdown"
                                  drop={"down"}
                                  onClick={handleStopPropagation}
                                  autoClose="outside"
                                  onToggle={(isOpen) => setShowDropdown(isOpen)}
                                >
                                  <Dropdown.Toggle>
                                    <Ellipsis className="text_black" />
                                  </Dropdown.Toggle>

                                  <Dropdown.Menu align="end">
                                    <Dropdown.Item
                                      href="#"
                                      onClick={() => {
                                        navigate(
                                          `/plan/offers/offer-details?id=${item?._id}`
                                        );
                                      }}
                                    >
                                      {t("View Details")}
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      href="#"
                                      onClick={() => {
                                        navigate(
                                          `/plan/offers/update-offer?id=${item?._id}`
                                        );
                                      }}
                                    >
                                      {t("Edit Offer")}
                                    </Dropdown.Item>

                                    <Dropdown.Item
                                      href="#"
                                      className={
                                        item.status === 1
                                          ? "text_danger"
                                          : "text_primary"
                                      }
                                      onClick={() => {
                                        handleStatusChange(
                                          item._id,
                                          item.status
                                        );
                                      }}
                                    >
                                      {item.status === 1
                                        ? t("Deactive")
                                        : t("Active")}
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Col>
            </Row>
          </div>
          {!loading && (
            <Paginations
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => {
                getAllData(page);
              }}
              totalCount={totalCount}
            />
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Offers;
